/**
 * Whether a component can be added to or removed from an entity, from the
 * rules its schema declares (`ComponentSchema.rules`). Pure functions, shared
 * by the add/remove commands (which refuse) and the Inspector (which offers
 * only what's allowed and says why not). Nothing here names a component.
 */
import type { EntityId, EntityRecord } from "../entity";
import type { ComponentRegistry } from "./ComponentRegistry";
import type { ComponentSchema } from "./defineComponent";

type Records = ReadonlyMap<EntityId, EntityRecord>;

/** Components the entity could take now, in Inspector order. */
export function addableComponents(
  registry: ComponentRegistry,
  entities: Records,
  id: EntityId,
): ComponentSchema[] {
  return registry
    .all()
    .filter((schema) => whyCantAdd(registry, entities, id, schema.type) === null);
}

/** Why `type` can't be added to the entity, or null if it can. */
export function whyCantAdd(
  registry: ComponentRegistry,
  entities: Records,
  id: EntityId,
  type: string,
): string | null {
  const schema = registry.get(type);
  if (!schema) return `Unknown component '${type}'`;
  const entity = entities.get(id);
  if (!entity) return `No entity '${id}'`;
  if (entity.components.has(type)) return `${entity.name} already has ${schema.category}`;

  return ruleViolation(registry, entities, entity, schema);
}

/**
 * Why an entity can't have the components it already has, or null if it can:
 * the same rules as adding them, checked for an entity that arrives whole
 * (created, pasted or duplicated) in `entities`.
 */
export function whyCantHave(
  registry: ComponentRegistry,
  entities: Records,
  id: EntityId,
): string | null {
  const entity = entities.get(id);
  if (!entity) return `No entity '${id}'`;

  for (const type of entity.components.keys()) {
    const schema = registry.get(type);
    if (!schema) return `Unknown component '${type}'`;
    const reason = ruleViolation(registry, entities, entity, schema);
    if (reason) return `${entity.name}: ${reason}`;
  }
  return null;
}

/** The first of a schema's rules that the entity, with that component, would break. */
function ruleViolation(
  registry: ComponentRegistry,
  entities: Records,
  entity: EntityRecord,
  schema: ComponentSchema,
): string | null {
  const rules = schema.rules ?? {};
  const elsewhere = (other: EntityRecord) =>
    other.id !== entity.id && other.components.has(schema.type);
  if (rules.perLevel && [...entities.values()].some(elsewhere))
    return `The level already has ${schema.category}`;

  const missing = (rules.requires ?? []).find((needed) => !entity.components.has(needed));
  if (missing) return `${schema.category} needs a ${categoryOf(registry, missing)} first`;

  const parentNeeds = rules.parentNeedsOneOf ?? [];
  const parent = entity.parent === null ? undefined : entities.get(entity.parent);
  if (parentNeeds.length > 0 && !parentNeeds.some((needed) => parent?.components.has(needed))) {
    const names = parentNeeds.map((needed) => categoryOf(registry, needed)).join(" or ");
    return `${schema.category} needs a parent with a ${names}`;
  }
  return null;
}

/** Why `type` can't be removed from the entity, or null if it can. */
export function whyCantRemove(
  registry: ComponentRegistry,
  entities: Records,
  id: EntityId,
  type: string,
): string | null {
  const entity = entities.get(id);
  if (!entity) return `No entity '${id}'`;
  const category = categoryOf(registry, type);
  if (!entity.components.has(type)) return `${entity.name} has no ${category}`;
  if (registry.get(type)?.rules?.required) return `${category} is required`;

  // Another component on the entity that needs this one keeps it.
  const dependent = [...entity.components.keys()].find((other) =>
    registry.get(other)?.rules?.requires?.includes(type),
  );
  if (dependent) return `${categoryOf(registry, dependent)} needs it`;
  return null;
}

/** Why `type` can't be turned off or on for the entity, or null if it can. */
export function whyCantToggle(
  registry: ComponentRegistry,
  entities: Records,
  id: EntityId,
  type: string,
): string | null {
  const entity = entities.get(id);
  if (!entity) return `No entity '${id}'`;
  const category = categoryOf(registry, type);
  if (!entity.components.has(type)) return `${entity.name} has no ${category}`;
  if (!registry.get(type)?.rules?.toggleable) return `${category} can't be turned off`;
  return null;
}

/** A component's Inspector title (its category), or its type if unknown. */
function categoryOf(registry: ComponentRegistry, type: string): string {
  return registry.get(type)?.category ?? type;
}
